import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Timer, Add } from '@mui/icons-material';
import TimeTracker from '../../components/TimeTracking/TimeTracker';
import { useAppSelector } from '../../hooks/useAppSelector';
import { mockTasks } from '../../entities/timeTracking/api/mockTimeTrackingData';
import './TimeTrackingPage.scss';

const TimeTrackingPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { activeTimer } = useAppSelector((state) => state.timeTracking);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [selectedTaskTitle, setSelectedTaskTitle] = useState<string>('');
  const [openCreateTaskDialog, setOpenCreateTaskDialog] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [tasks, setTasks] = useState(mockTasks);

  const handleTaskChange = (taskId: string) => {
    setSelectedTaskId(taskId);
    const task = tasks.find(t => t.id === taskId);
    setSelectedTaskTitle(task?.title || '');
  };

  const handleOpenCreateTaskDialog = () => {
    setNewTaskTitle('');
    setOpenCreateTaskDialog(true);
  };

  const handleCloseCreateTaskDialog = () => {
    setOpenCreateTaskDialog(false);
    setNewTaskTitle('');
  };

  const handleCreateTask = () => {
    if (!newTaskTitle.trim()) {
      return;
    }

    // Create new task
    const newTask = {
      id: `task-${Date.now()}`,
      title: newTaskTitle.trim(),
      boardId: 'board-1',
    };

    // Add to tasks list
    setTasks([...tasks, newTask]);

    // Auto-select the newly created task
    setSelectedTaskId(newTask.id);
    setSelectedTaskTitle(newTask.title);

    // Close dialog
    handleCloseCreateTaskDialog();
  };

  return (
    <Box className="time-tracking-page">
      <Box className="time-tracking-page__header">
        <Timer sx={{ fontSize: 48, color: '#4A90E2', mb: 2 }} />
        <Typography variant="h4" className="time-tracking-page__title">
          Time Tracking
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track time spent on tasks and view your time logs
        </Typography>
      </Box>

      <Box className="time-tracking-page__content">
        {/* Task Selector */}
        <Box sx={{ mb: 3, maxWidth: 600 }}>
          <Alert severity={activeTimer ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {activeTimer
              ? '⏱️ Timer is running! Stop the timer before switching tasks or creating new ones.'
              : 'Select a task below to start tracking time or log time manually'}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <FormControl fullWidth disabled={!!activeTimer}>
              <InputLabel id="task-select-label">Select Task</InputLabel>
              <Select
                labelId="task-select-label"
                id="task-select"
                value={selectedTaskId}
                label="Select Task"
                onChange={(e) => handleTaskChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {tasks.map((task) => (
                  <MenuItem key={task.id} value={task.id}>
                    {task.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleOpenCreateTaskDialog}
              sx={{ minWidth: '160px', height: '56px' }}
              disabled={!!activeTimer}
              title={activeTimer ? 'Stop the timer before creating a new task' : 'Create a new task'}
            >
              New Task
            </Button>
          </Box>
        </Box>

        {/* Time Tracker Component */}
        <TimeTracker
          taskId={selectedTaskId || undefined}
          taskTitle={selectedTaskTitle || undefined}
          userId={user?.id || '1'}
          userName={user?.name || 'Current User'}
        />
      </Box>

      {/* Create Task Dialog */}
      <Dialog
        open={openCreateTaskDialog}
        onClose={handleCloseCreateTaskDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label="Task Title"
              placeholder="Enter task title (e.g., Fix authentication bug)"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTaskTitle.trim()) {
                  handleCreateTask();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateTaskDialog}>Cancel</Button>
          <Button
            onClick={handleCreateTask}
            variant="contained"
            disabled={!newTaskTitle.trim()}
          >
            Create Task
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingPage;
